<script setup>
import {ErrorMessage, Field} from 'vee-validate';
import {IMaskComponent} from 'vue-imask';

const VImask = IMaskComponent;

const props = defineProps({
  fields: {type: Array, required: true}
});

const showPassword = ref({});

const togglePasswordVisibility = (fieldName) => {
  showPassword.value[fieldName] = !showPassword.value[fieldName];
};

const getColClass = (size) => {
  return size ? `col-sm-${size.sm} col-md-${size.md}` : 'col-12';
};

// Converte de dd/MM/yyyy para yyyy-MM-ddT12:00:00.000 (meio-dia, evita mudança de dia)
const formatDateToUS = (dateBR) => {
  if (!dateBR || dateBR.length !== 10) return '';
  const [day, month, year] = dateBR.split('/');
  return `${year}-${month}-${day}T12:00:00.000`;
};

// Converte de yyyy-MM-ddTHH:mm:ss.SSS para dd/MM/yyyy
const formatDateToBR = (dateUS) => {
  if (!dateUS) return '';
  const dateOnly = dateUS.split('T')[0];
  if (dateOnly.length !== 10) return '';
  const [year, month, day] = dateOnly.split('-');
  return `${day}/${month}/${year}`;
};

// Converte de dd/MM/yyyy HH:mm para yyyy-MM-ddTHH:mm:ss-03:00 (UTC-3)
const formatDateTimeToUS = (dateTimeBR) => {
  if (!dateTimeBR || dateTimeBR.length !== 16) return '';
  const [datePart, timePart] = dateTimeBR.split(' ');
  const [day, month, year] = datePart.split('/');
  return `${year}-${month}-${day}T${timePart}`;
};

// Converte de yyyy-MM-ddTHH:mm:ss (qualquer timezone) para dd/MM/yyyy HH:mm (UTC-3)
const formatDateTimeToBR = (dateTimeUS) => {
  if (!dateTimeUS) return '';
  const date = new Date(dateTimeUS);
  const utcMinus3 = new Date(date.getTime() - (3 * 60 * 60 * 1000));
  const day = String(utcMinus3.getUTCDate()).padStart(2, '0');
  const month = String(utcMinus3.getUTCMonth() + 1).padStart(2, '0');
  const year = utcMinus3.getUTCFullYear();
  const hours = String(utcMinus3.getUTCHours()).padStart(2, '0');
  const minutes = String(utcMinus3.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const getMaskByType = (type) => {
  switch (type) {
    case 'cpfCnpj':
      return [
        { mask: '000.000.000-00', min: 0, max: 11 },
        { mask: '00.000.000/0000-00', min: 12, max: 14 }
      ]
    case 'cpf':
      return '000.000.000-00';
    case 'cnpj':
      return '00.000.000/0000-00';
    case 'cep':
      return '00.000-000';
    case 'phone':
      return '+55 (00) 00000-0000';
    case 'date':
      return '00/00/0000';
    case 'credit-card':
      return '0000 0000 0000 0000';
    case 'time':
      return '00:00';
    case 'datetime':
      return '00/00/0000 00:00';
    default:
      return '';
  }
};
</script>

<template>
  <div class="row g-3">
    <div v-for="(config, key) in fields" :key="config.name" :class="getColClass(config.size)">
      <label v-show="!['checkbox', 'radio', 'hidden', 'slot', 'html'].includes(config.type)" :for="config.name"
             class="form-label">
        {{ config.label }}
      </label>

      <Field :name="config.name" v-slot="{ field, handleChange, value }">

        <div v-if="config.customFieldSlot">
          <slot :name="config.customFieldSlot"/>
        </div>

        <div v-if="config.type === 'html'" v-html="config.label"/>

        <input
            v-else-if="['text', 'email'].includes(config.type)"
            v-bind="field"
            class="form-control form-control-sm"
            :placeholder="config.label"
            :disabled="config.disabled"
            @blur="config.onBlurEvent?.(value)"
            @change="config.onChangeEvent?.(value)"
        />

        <input
            v-else-if="config.type === 'money'"
            v-mask-decimal.br="2"
            :name="field.name"
            :value="value"
            class="form-control form-control-sm"
            :placeholder="config.label"
            :disabled="config.disabled"
            @input="(e) => {
              const formattedValue = e.target.value;
              const numericValue = formattedValue
                .replace(/\./g, '')
                .replace(',', '.');
              handleChange(numericValue);
            }"
            @blur="config.onBlurEvent?.(value)"
            @change="config.onChangeEvent?.(value)"
        />

        <textarea
            v-else-if="config.type === 'textarea'"
            v-bind="field"
            class="form-control form-control-sm"
            rows="5"
            :placeholder="config.label"
            :disabled="config.disabled"
            @blur="config.onBlurEvent?.(value)"
            @change="config.onChangeEvent?.(value)"
        />

        <select
            v-else-if="config.type === 'select'"
            v-bind="field"
            class="form-select form-select-sm"
            :disabled="config.disabled"
            @blur="config.onBlurEvent?.(value)"
            @change="config.onChangeEvent?.(value)"
        >
          <option value="" disabled selected>Selecione...</option>
          <option v-for="opt in config.selectOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>

        <div v-else-if="['checkbox', 'radio', 'hidden'].includes(config.type)" class="form-check">
          <input
              v-bind="field"
              :type="config.type"
              :value="true"
              :unchecked-value="false"
              class="form-check-input"
              :disabled="config.disabled"
              :checked="value === true"
              @change="(e) => {
                handleChange(e.target.checked);
                config.onChangeEvent?.(e.target.checked);
              }"
          />
          <label class="form-check-label" :for="`sff-${key}-${field.name}`">
            <span v-html="config.label"/>
          </label>
        </div>

        <div class="input-group" v-if="config.type === 'password'">
          <input
              :type="showPassword[field.name] ? 'text' : 'password'"
              v-bind="field"
              class="form-control form-control-sm"
              :placeholder="config.label"
              :disabled="config.disabled"
              autocomplete="off"
              @blur="config.onBlurEvent?.(value)"
              @change="config.onChangeEvent?.(value)"
          />
          <span class="input-group-text">
            <a @click.prevent="togglePasswordVisibility(field.name)">
              <Icon v-if="!showPassword[field.name]" icon="mi:eye-off"/>
              <Icon v-else icon="mi:eye"/>
            </a>
          </span>
        </div>

        <template v-if="config.type === 'upload'">
          <uploader
              :model-value="value"
              :accept="config.accept || 'image/*'"
              :disabled="config.disabled"
              @update:model-value="(file) => {
                handleChange(file, true);
                config.onChangeEvent?.(file);
              }"
              @blur="config.onBlurEvent?.(value)"
          />
        </template>

        <MultiSelect
            v-if="config.type === 'multi-select'"
            :name="field.name"
            :disabled="config.disabled"
            :options="config.selectOptions"
            placeholder="Selecione..."
            :model-value="value || []"
            @update:model-value="(selectedArray) => {
              handleChange(selectedArray);
              config.onChangeEvent?.(selectedArray);
            }"
            @blur="config.onBlurEvent?.(value)"
        />

        <template v-else>
          <client-only>
            <v-imask
                v-if="['cpf', 'cep', 'phone', 'cnpj', 'credit-card', 'cpfCnpj'].includes(config.type)"
                class="form-control form-control-sm"
                :model-value="value"
                @accept="(val) => handleChange(val)"
                :mask="getMaskByType(config.type)"
                :disabled="config.disabled"
                @blur="config.onBlurEvent?.(value)"
                @change="config.onChangeEvent?.(value)"
            />

            <v-imask
                v-else-if="config.type === 'date'"
                class="form-control form-control-sm"
                :model-value="formatDateToBR(value)"
                @accept="(val) => handleChange(formatDateToUS(val))"
                mask="00/00/0000"
                placeholder="dd/mm/aaaa"
                :disabled="config.disabled"
            />

            <v-imask
                v-else-if="config.type === 'datetime'"
                class="form-control form-control-sm"
                :model-value="formatDateTimeToBR(value)"
                @accept="(val) => handleChange(formatDateTimeToUS(val))"
                mask="00/00/0000 00:00"
                placeholder="dd/mm/aaaa hh:mm"
                :disabled="config.disabled"
            />

            <HtmlEditor
                v-if="config.type === 'html-editor'"
                :name="field.name"
                :disabled="config.disabled"
                :model-value="value || ''"
                @update:model-value="(content) => {
                  handleChange(content);
                  config.onChangeEvent?.(content);
                }"
                @blur="config.onBlurEvent?.(value)"
            />

            <template #fallback>
              <input class="form-control" :placeholder="config.label" readonly/>
            </template>
          </client-only>
        </template>
      </Field>

      <ErrorMessage :name="config.name" class="text-danger small mt-1 d-block"/>
    </div>
  </div>
</template>
